import { ISession, Visibility } from './session';

export interface RecordingTranscriptItem {
  text: string;
  start: number;
  end: number;
  userId?: string;
}

export interface RecordingTranscriptUser {
  _id: string;
  userId?: string;
  name?: string;
  email?: string;
  profile_image?: string;
  personas?: string[];
}

// New data structures matching the backend ISpaceRecordingInsights
export interface IScoreBand {
  label: string;
  min: number;
  max: number;
  description?: string;
  color?: string;
}

export interface IScoreScale {
  min: number;
  max: number;
  precision?: number;
  bands?: IScoreBand[];
}

export interface IBandDisplay {
  label: string;
  color?: string;
}

export interface IScoring {
  skillCategory: string;
  score: number;
  reason: string;
  band?: IBandDisplay | null;
}

export enum TutorCoachInsightType {
  IMPROVEMENT = 'IMPROVEMENT',
  ENCOURAGEMENT = 'ENCOURAGEMENT',
}

export enum FeedbackState {
  HELPFUL,
  NOT_HELPFUL,
  UNSELECTED,
}

export interface BaseOccurrenceCase {
  skillCategory: string;
  occurrence: number;
  impactScore: number;
  recordingId: string;
}

export interface ITutorCoachImprovementInsight extends BaseOccurrenceCase {
  _type: TutorCoachInsightType.IMPROVEMENT;
  details: {
    observation: string;
    suggestion: string;
    example: string;
  };
}

export interface ITutorCoachEncouragementInsight extends BaseOccurrenceCase {
  _type: TutorCoachInsightType.ENCOURAGEMENT;
  details: {
    celebration: string;
  };
}

export type ITutorCoachInsight = ITutorCoachImprovementInsight | ITutorCoachEncouragementInsight;

export enum SpaceRecordingInsightStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  MEETING_IRRELEVANT = 'meeting_irrelevant',
  DISABLED = 'disabled',
  MEETING_TOO_SHORT = 'meeting_too_short',
  NO_TRANSCRIPT = 'no_transcript',
  /** Backend's SessionReportStatus resting value (Events.ReportStatus.NOT_APPLICABLE) — the
   * pipeline hasn't started, failed before writing anything, or was never queued. */
  NOT_APPLICABLE = 'not_applicable',
}

export interface ISpaceRecordingInsights {
  _id: string;
  /** Legacy 1:1 schema — present on older insight docs. */
  // TODO: @ameyrathi remove this field after migration is complete
  recordingId?: string;
  /** New session-based schema — one insights doc covers all recordings in a session. */
  recordingIds?: string[];
  sessionId?: string;
  _type: string;
  scoring: IScoring[];
  insights: ITutorCoachInsight[];
  status?: SpaceRecordingInsightStatus;
  hostId?: string | null;
  overallScore?: number;
  overallNormalisedScore?: number;
  overallBand?: IBandDisplay | null;
  coachConfig?: { scale: IScoreScale };
}

/** A single safeguarding item flagged during analysis. Mirrors backend ISafeguardingItem. */
export interface ISafeguardingItem {
  source: string;
  timestamp: string;
  recordingId: string | null;
  recordingTimestamp: number;
  roomId: string;
  userId: string;
  itemTitle: string;
  itemSeverity: string;
  itemContent: string;
}

/**
 * Space recording safeguarding insights document as returned by the API.
 * Mirrors backend SpaceRecordingSafeguardingInsights (ids serialized as strings).
 */
export interface SpaceRecordingSafeguardingInsights {
  _id: string;
  sessionId?: string;
  spaceId?: string;
  recordingIds?: string[];
  items?: ISafeguardingItem[];
  createdAt?: string;
  updatedAt?: string;
}

export enum SpaceRecordingSnippetsStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  MEETING_TOO_SHORT = 'meeting_too_short',
  MEETING_IRRELEVANT = 'meeting_irrelevant',
  DISABLED = 'disabled',
  PENDING = 'pending',
  NO_TRANSCRIPT = 'no_transcript',
  /** Backend's SessionReportStatus resting value (Events.ReportStatus.NOT_APPLICABLE) — the
   * pipeline hasn't started, failed before writing anything, or was never queued. */
  NOT_APPLICABLE = 'not_applicable',
}

export interface ISnippetsItem {
  _id: string;
  recordingId: string;
  roomId: string;
  recordingOffsetStart: number;
  recordingOffsetEnd: number;
  title: string;
  description: string;
  category: string;
  thumbnailUrl: string;
  participantId: string;
  approved: boolean;
}

export interface ISpaceRecordingSnippets {
  _id?: string;
  sessionId?: string;
  recordingIds?: string[];
  schemaVersion?: string;
  items: ISnippetsItem[];
  allItemsDeletedByAdmin?: boolean;
}

export enum RecordingSummaryStatus {
  GENERATED = 'GENERATED',
  DISABLED = 'DISABLED',
  MEETING_TOO_SHORT = 'MEETING_TOO_SHORT',
  PROCESSING = 'PROCESSING',
  NO_TRANSCRIPT = 'NO_TRANSCRIPT',
  FAILED = 'FAILED',
  /** Backend's SessionReportStatus resting value (Events.ReportStatus.NOT_APPLICABLE) — the
   * pipeline hasn't started, failed before writing anything, or was never queued. */
  NOT_APPLICABLE = 'not_applicable',
}

export interface RecordingSummary {
  /** Which summarization prompt produced this summary; use for render selection. */
  promptName?: string;
  headline?: string;
  summary?: string;
  tasks?: string[];
  summaryStatus?: RecordingSummaryStatus;
  /** Prompt-specific fields (e.g. key_points, decisions); render via promptName. */
  extraFields?: Record<string, unknown>;
}

export enum SpaceRecordingStatus {
  IN_PROGRESS = 'in-progress',
  FINISHED = 'finished',
  FAILED = 'failed',
}

/** Groups recordings by room in first-seen order; each group is sorted by start time. */
export function groupRecordingsByRoom(recordings: SpaceRecording[]): SpaceRecording[][] {
  const groups = new Map<string, SpaceRecording[]>();
  for (const recording of recordings) {
    const group = groups.get(recording.roomId);
    if (group) {
      group.push(recording);
    } else {
      groups.set(recording.roomId, [recording]);
    }
  }
  return Array.from(groups.values()).map((group) =>
    [...group].sort(
      (a, b) => new Date(a.startingTime).getTime() - new Date(b.startingTime).getTime(),
    ),
  );
}

export interface SpaceRecording {
  recordingId: string;
  spaceId: string;
  roomId: string;
  startingTime: string;
  duration: number;
  streamingLink: string;
  access?: string;
  spaceName?: string;
  roomName?: string;
  formattedStartTime?: string;
  space?: ISession;
  sessionTitle?: string;
  status?: SpaceRecordingStatus;
  sessionId?: string;
  users?: RecordingTranscriptUser[];
  transcript?: RecordingTranscriptItem[];
  insights?: ISpaceRecordingInsights[];
  summary?: RecordingSummary | null;
  spaceVisibility?: Visibility | null;
  /** Session-level report status — only the fields the caller has access to are populated. */
  reportStatus?: {
    coachingStatus?: SpaceRecordingInsightStatus;
    summaryStatus?: RecordingSummaryStatus;
  };
}

export interface InstitutionRecordingsReport {
  recordings: SpaceRecording[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

export enum RecordingAccess {
  ADMINS = 'admins',
  HOSTS = 'hosts',
  HOSTS_AND_PARTICIPANTS = 'hosts-and-participants',
  PUBLIC = 'public',
}
